import { useState, useEffect } from "react";
import { useAdmin } from "@/contexts/AdminContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Eye, EyeOff, Edit2 } from "lucide-react";

export default function AdminInsights() {
  const { api } = useAdmin();
  const [insights, setInsights] = useState([]);
  const [searchParams] = useSearchParams();

  const load = () => api.get("/admin/insights").then(r => setInsights(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // If ?new=true, redirect to new insight
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      createNew();
    }
  }, [searchParams]);

  const createNew = async () => {
    try {
      const res = await api.post("/admin/insights", {
        title: "Untitled Insight",
        category: "Article",
        tags: [],
        excerpt: "",
        content: "",
        author: "Equaliser Legal LLP",
        status: "draft"
      });
      window.location.href = `/admin/insights/${res.data.id}`;
    } catch { toast.error("Failed to create"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this insight?")) return;
    try { await api.delete(`/admin/insights/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const toggleStatus = async (insight) => {
    const statusCycle = { draft: "preview", preview: "published", published: "draft" };
    const next = statusCycle[insight.status] || "draft";
    try {
      await api.put(`/admin/insights/${insight.id}`, { status: next });
      toast.success(`Status changed to ${next}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const statusColors = { draft: "bg-yellow-50 text-yellow-600", preview: "bg-blue-50 text-blue-600", published: "bg-green-50 text-green-600" };

  return (
    <div data-testid="admin-insights">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl text-[#0A1628] font-medium">Insights</h1>
        <button onClick={createNew} data-testid="new-insight" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]">
          <Plus size={14} /> New Insight
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2px] overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Title</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Category</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Status</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Date</th>
              <th className="text-right text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {insights.map(ins => (
              <tr key={ins.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Link to={`/admin/insights/${ins.id}`} className="text-[#0A1628] text-sm font-medium hover:text-[#C5A059]">{ins.title}</Link>
                  {ins.documents && ins.documents.length > 0 && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{ins.documents.length} docs</span>}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{ins.category}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleStatus(ins)} className={`text-[10px] px-2 py-0.5 rounded-[2px] cursor-pointer ${statusColors[ins.status] || "bg-slate-50 text-slate-400"}`} data-testid={`status-toggle-${ins.id}`}>
                    {ins.status}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">{ins.published_date}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {ins.status === "preview" && (
                      <a href={`/insights/preview/${ins.id}`} target="_blank" rel="noopener" className="text-xs text-blue-500 hover:text-blue-700 border border-slate-200 px-2 py-1 rounded-[2px]" title="Preview"><Eye size={12} /></a>
                    )}
                    <Link to={`/admin/insights/${ins.id}`} className="text-xs text-slate-500 hover:text-[#0A1628] border border-slate-200 px-2 py-1 rounded-[2px]"><Edit2 size={12} /></Link>
                    <button onClick={() => remove(ins.id)} className="text-xs text-red-400 hover:text-red-600 border border-slate-200 px-2 py-1 rounded-[2px]"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {insights.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">No insights yet.</p>}
      </div>
    </div>
  );
}
